import LotCountGetter from "components/CustomGridItems/LotCountGetter";
import DataDumpGetterSinceLotStarted from "components/CustomGridItems/LotInfoBar/DataDumpGetterSinceLotStarted";
import LotInfoGetter from "components/CustomGridItems/LotInfoGetter";
import HubletViewDataDumpGetter from "components/Dashboard/HubletView/HubletViewDataDumpGetter";
import HubletViewDataDumpRollingWindowGetter from "components/Dashboard/HubletView/HubletViewDataDumpRollingWindowGetter";
import {
  useDataSourceGroups,
  useIsTimelineExpanded,
} from "components/Dashboard/HubletView/SingleTimeline/singleTimelineStore";
import TrendChartBarsDataDumpsGetter from "components/Dashboard/TrendChart/TrendChartBarsGetter";
import {
  CUSTOMERS_WHO_USE_SUPERVISOR_CONTEXT_TAGGING,
  OVERRIDE_MACHEMETRY_IDS_FOR_OTHER_PROCESSES_PNL_CHART,
} from "constants/customers";
import { useSettings } from "contexts/SettingsManager/SettingsManager";
import { CustomerInfoJsonGetter } from "components/DataGetter/CustomerJsonGetter";
import AggregatesAndSegmentsGettersRollupers from "segments/AggregatesAndSegmentsGettersRollupers";

import { useForceMidgardBreakerOpen, useIsLotInfoPaused } from "store/atoms";
import { useSelector } from "store/useSelector";
import {
  useIsSingleProcessPage,
  useGetPageFromPath,
  useFocusRange,
  useVisibleGroups,
  useLoadWhenValuesChange,
  useVisibleProcessIds,
  useIsSerialProcess,
  useVisibleProcesses,
  useIsAdmin,
  useWorkScheduleIdOnCurrentPage,
  useMidgardIdHubletPage,
  useForceUpdateOnInterval,
} from "utils/customHooks/genericHooks";
import { useIsStorybook } from "utils/customHooks/storyHooks";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import useFlagsWithDefaults, {
  hasLDClientBeenIdentifiedAtom,
} from "utils/customHooks/useFlagsWithDefaults";
import {
  useIsFromSnapshotsPageTimelineOnly,
  useShouldFetchCustomTrendChartDataDumpsForCount,
  useShouldFetchTrendChartDataDumps,
  useShouldUseDataDumpForHubletView,
  useShouldUseDataDumpsForGroupMetrics,
} from "utils/customHooks/whichServerHooks";
import { ONE_HOUR_OF_MS } from "utils/timeUtils";
import { AllExpandedProcessTilesGetter } from "./AllExpandedProcessTilesGetter";
import { DataDumpGetterForFullRange } from "./DataDumpGetterForFullRange";
import { DataDumpGetterForFullRangePerProcess } from "./DataDumpGetterForFullRangePerProcess";
import { DataDumpGetterForFullRangeSubordinateProcesses } from "./DataDumpGetterForFullRangeSubordinateProcesses";
import { DataDumpGettersForGroupTiles } from "./DataDumpGettersForGroupTiles";
import { DataDumpRowsGetter } from "./DataDumpRowsGetter";
import { ShiftsGetterConnected } from "./ShiftsGetterConnected";
import { SupervisorAllocationGetterConnected } from "./SupervisorAllocationGetterConnected";
import {
  AlternateTimelinesGetter,
  DataSourceGroupsGetter,
} from "./DataSourceGroupsGetter";
import { useProcessIdsForDataDumpFullRange } from "./useProcessIdsForDataDumpFullRange";
import ProcessGroupsGetter, {
  didFetchProcessGroupsAtom,
} from "./ProcessGroupsGetter";
import { getIsCypressRunning } from "constants/constants";
import { useServiceStatus } from "components/SimpleComponents/TopBanner/useServiceStatus";
import { MidgardStatusChecker } from "./MidgardStatusChecker";
import { ForceMidgardBreakerOpenOnce } from "./ForceMidgardBreakerOpenOnce";
import { CustomersListGetter } from "pages/CustomerTilesPage/useCustomersQuery";
import { useLocalStorageState } from "utils/useLocalStorageState";
import { Fragment, useEffect, useMemo } from "react";
import { useAtom } from "jotai";
import { FlightConfigGetter } from "./FlightConfig/FlightConfigGetter";
import LastDataDumpRowGetter, {
  LastDataDumpRowPerProcessGetter,
  useLastDataDumpRow,
} from "./LastDataDumpRowGetter";
import { CurrentSegmentGetter } from "./CurrentSegmentGetter";
import { useIsMidgardDDAEnabled } from "components/Dashboard/GroupMetrics/useMetrics";
import { useExpandedTileState } from "components/Dashboard/Tiles/ProcessTile/ExpandedTile/ExpandedTileContent";
import { useExpandedTileFlags } from "components/Dashboard/Tiles/ProcessTile/processTileUtils";
import { useFlightConfig } from "./FlightConfig/useFlightConfig";
import { CustomMetricsAggregatesGetter } from "./CustomMetricsAggregatesGetter";
import TrendChartBarsCountsDataDumpsGetter from "components/Dashboard/TrendChart/TrendChartBarsCountsGetter";
import ErrorBoundary from "components/SimpleComponents/Errors/ErrorBoundary";
import { WorkSchedulesGetter } from "./WorkSchedulesGetter";
import { CurrentPlaybookGetter } from "./CurrentPlaybookGetter";
import { hasAuthenticationCompletedAtom } from "App/AuthenticationHandler";
import {
  useIsPnlChartExpandedHubletPage,
  useIsTrendChartExpandedHubletPage,
} from "pages/DashboardPages/dashboardPagesUtils";
import { useProcessIdOnSingleProcessPage } from "components/CustomGridItems/LotInfoBar/lotInfoUtils";
import { HUBLET_VIEW_DATA_DUMP_POLL_INTERVAL } from "constants/intervals";

/** fetches all the data we need depending on the current page */
const DataGetter = () => {
  const [hasAuthenticationCompleted] = useAtom(hasAuthenticationCompletedAtom);
  const isStorybook = useIsStorybook();
  if (!hasAuthenticationCompleted && !isStorybook && !getIsCypressRunning()) {
    return null;
  }
  return (
    <>
      <ErrorBoundary small={true} overlay={true} id="AskrGetters">
        <AskrGetters />
      </ErrorBoundary>
      <ErrorBoundary small={true} overlay={true} id="MachemetryGetters">
        <MachemetryGetters />
      </ErrorBoundary>
      <ErrorBoundary small={true} overlay={true} id="MidgardGetters">
        <MidgardGetters />
      </ErrorBoundary>
    </>
  );
};

function AskrGetters() {
  return (
    <>
      <ShiftsGetterWrapper />
      <AggregatesAndSegmentsGetterWrapper />
      <CurrentSegmentGetterWrapper />
    </>
  );
}

function ShiftsGetterWrapper() {
  const shouldFetch = useShouldFetchShifts();
  if (!shouldFetch) {
    return null;
  }
  return (
    <ErrorBoundary small={true} overlay={true} id="AskrGetters_ShiftsGetter">
      <ShiftsGetterConnected />
    </ErrorBoundary>
  );
}

function AggregatesAndSegmentsGetterWrapper() {
  const shouldFetch = useShouldFetchAggregatesAndSegments();
  if (!shouldFetch) {
    return null;
  }
  return (
    <ErrorBoundary
      small={true}
      overlay={true}
      id="AskrGetters_AggregatesSegmentsGetters"
    >
      <AggregatesAndSegmentsGettersRollupers />
    </ErrorBoundary>
  );
}

function CurrentSegmentGetterWrapper() {
  const shouldFetch = useShouldFetchCurrentSegment();
  if (!shouldFetch) {
    return null;
  }
  return (
    <ErrorBoundary
      small={true}
      overlay={true}
      id="AskrGetters_CurrentSegmentGetter"
    >
      <CurrentSegmentGetter />
    </ErrorBoundary>
  );
}
export function useShouldFetchShifts() {
  const workScheduleId = useWorkScheduleIdOnCurrentPage();
  const visibleProcesses = useVisibleProcesses();
  const ready = visibleProcesses.length && Boolean(workScheduleId);
  return ready;
}
export function useShouldFetchCurrentSegment() {
  const workScheduleId = useWorkScheduleIdOnCurrentPage();
  const visibleProcesses = useVisibleProcesses();
  const isSingleProcessPage = useIsSingleProcessPage();
  const ready =
    visibleProcesses.length && Boolean(workScheduleId) && isSingleProcessPage;
  return ready;
}

function MachemetryGetters() {
  return (
    <>
      <ErrorBoundary
        small={true}
        overlay={true}
        id="MachemetryGetters_CustomersListGetter"
      >
        <CustomersListGetter />
      </ErrorBoundary>
    </>
  );
}

function MidgardGetters() {
  const [
    {
      midgard: { breakerOpen },
    },
  ] = useServiceStatus();
  const [forceMidgardBreakerOpen] = useForceMidgardBreakerOpen();

  return (
    <>
      <ErrorBoundary
        small={true}
        overlay={true}
        id="MidgardGetters_ForceReload"
      >
        <ForceReloadAppForCustomer />
      </ErrorBoundary>
      <ErrorBoundary
        small={true}
        overlay={true}
        id="MidgardGetters_CustomerInfoJsonGetter"
      >
        <CustomerInfoJsonGetter />
      </ErrorBoundary>
      <ErrorBoundary
        small={true}
        overlay={true}
        id="MidgardGetters_FlightConfigGetter"
      >
        <FlightConfigGetter />
      </ErrorBoundary>
      <ErrorBoundary
        small={true}
        overlay={true}
        id="MidgardGetters_CustomMetricsAggregatesGetter"
      >
        <CustomMetricsAggregatesGetter />
      </ErrorBoundary>
      {/* use <ForceMidgardBreakerOpenOnce/> to manually test the ServiceOutageIndicator */}
      {/* ⚠ only use with fake customers (raveng, ravenjest) */}
      <ErrorBoundary
        small={true}
        overlay={true}
        id="MidgardGetters_ForceMidgardBreakerOpenOnce"
      >
        {forceMidgardBreakerOpen && <ForceMidgardBreakerOpenOnce />}
      </ErrorBoundary>
      <ErrorBoundary
        small={true}
        overlay={true}
        id="MidgardGetters_MidgardStatusChecker"
      >
        {breakerOpen && <MidgardStatusChecker />}
      </ErrorBoundary>
      <ErrorBoundary
        small={true}
        overlay={true}
        id="MidgardGetters_breakerOpen"
      >
        {!breakerOpen && (
          <>
            <ProcessGroupsGetterWrapper />
            <MidgardLotDataWrapper />
            <FullRangeDataDumpWrapper />
            <LotProgressWrapper />
            <SupervisorContextWrapper />
            <HubletViewDataDumpWrapper />
            <DataDumpsPerProcessWrapper />
            <DataDumpSubordinateProcessesWrapper />
            <TrendChartDataDumpsWrapper />
            <CustomTrendChartDataDumpsForCountWrapper />
            <ExpandedTilesDataWrapper />
            <DataDumpForGroupTilesWrapper />
            <TimelineDataDumpRowsWrapper />
            <DataSourceGroupsWrapper />
            <AlternateTimelinesWrapper />
            <LastDataDumpRowWrapper />
            <LastDataDumpRowPerProcessWrapper />
            <WorkSchedulesWrapper />
            <CurrentPlaybookWrapper />
          </>
        )}
      </ErrorBoundary>
    </>
  );
}

function ProcessGroupsGetterWrapper() {
  return (
    <ErrorBoundary
      small={true}
      overlay={true}
      id="MidgardGetters_ProcessGroupsGetter"
    >
      <ProcessGroupsGetter />
    </ErrorBoundary>
  );
}

function MidgardLotDataWrapper() {
  const shouldFetch = useShouldFetchMidgardLotData();
  if (!shouldFetch) {
    return null;
  }
  return (
    <ErrorBoundary
      small={true}
      overlay={true}
      id="MidgardGetters_LotInfoGetter"
    >
      <LotInfoGetter />
      <LotCountGetter />
    </ErrorBoundary>
  );
}

function FullRangeDataDumpWrapper() {
  const shouldFetch = useShouldFetchFullRangeDataDump();
  if (!shouldFetch) {
    return null;
  }
  return (
    <ErrorBoundary
      small={true}
      overlay={true}
      id="MidgardGetters_DataDumpGetterForFullRange"
    >
      <DataDumpGetterForFullRange />
    </ErrorBoundary>
  );
}

function LotProgressWrapper() {
  const shouldFetch = useShouldFetchLotProgress();
  if (!shouldFetch) {
    return null;
  }
  return (
    <ErrorBoundary
      small={true}
      overlay={true}
      id="MidgardGetters_DataDumpGetterSinceLotStarted"
    >
      <DataDumpGetterSinceLotStarted />
    </ErrorBoundary>
  );
}

function SupervisorContextWrapper() {
  const shouldFetch = useShouldFetchSupervisorContext();
  if (!shouldFetch) {
    return null;
  }
  return (
    <ErrorBoundary
      small={true}
      overlay={true}
      id="MidgardGetters_SupervisorAllocationGetterConnected"
    >
      <SupervisorAllocationGetterConnected />
    </ErrorBoundary>
  );
}

function HubletViewDataDumpWrapper() {
  const shouldFetch = useShouldUseDataDumpForHubletView();
  const { isUsingRateRollingTimeWindow } = useSettings();
  const randKey = useForceUpdateOnInterval({
    updateInterval: HUBLET_VIEW_DATA_DUMP_POLL_INTERVAL,
  });
  if (!shouldFetch) {
    return null;
  }
  return (
    <Fragment key={randKey}>
      <ErrorBoundary
        small={true}
        overlay={true}
        id="MidgardGetters_HubletViewDataDumpGetter"
      >
        <HubletViewDataDumpGetter />
      </ErrorBoundary>
      {isUsingRateRollingTimeWindow && (
        <ErrorBoundary
          small={true}
          overlay={true}
          id="MidgardGetters_HubletViewDataDumpRollingWindowGetter"
        >
          <HubletViewDataDumpRollingWindowGetter />
        </ErrorBoundary>
      )}
    </Fragment>
  );
}

function DataDumpsPerProcessWrapper() {
  const shouldFetch = useShouldFetchDataDumpsPerProcess();
  if (!shouldFetch) {
    return null;
  }
  return (
    <ErrorBoundary
      small={true}
      overlay={true}
      id="MidgardGetters_DataDumpGetterForFullRangePerProcess"
    >
      <DataDumpGetterForFullRangePerProcess />
    </ErrorBoundary>
  );
}

function DataDumpSubordinateProcessesWrapper() {
  const shouldFetch = useShouldFetchDataDumpSubordinateProcesses();
  if (!shouldFetch) {
    return null;
  }
  return (
    <ErrorBoundary
      small={true}
      overlay={true}
      id="MidgardGetters_DataDumpGetterForFullRangeSubordinateProcesses"
    >
      <DataDumpGetterForFullRangeSubordinateProcesses />
    </ErrorBoundary>
  );
}

function TrendChartDataDumpsWrapper() {
  const shouldFetch = useShouldFetchTrendChartDataDumps();
  if (!shouldFetch) {
    return null;
  }
  return (
    <ErrorBoundary
      small={true}
      overlay={true}
      id="MidgardGetters_TrendChartBarsDataDumpsGetter"
    >
      <TrendChartBarsDataDumpsGetter />
    </ErrorBoundary>
  );
}

function CustomTrendChartDataDumpsForCountWrapper() {
  const shouldFetch = useShouldFetchCustomTrendChartDataDumpsForCount();
  if (!shouldFetch) {
    return null;
  }
  return (
    <ErrorBoundary
      small={true}
      overlay={true}
      id="MidgardGetters_TrendChartBarsCountsDataDumpsGetter"
    >
      <TrendChartBarsCountsDataDumpsGetter />
    </ErrorBoundary>
  );
}

function ExpandedTilesDataWrapper() {
  const shouldFetch = useShouldFetchExpandedTilesData();
  if (!shouldFetch) {
    return null;
  }
  return (
    <ErrorBoundary
      small={true}
      overlay={true}
      id="MidgardGetters_AllExpandedProcessTilesGetter"
    >
      <AllExpandedProcessTilesGetter />
    </ErrorBoundary>
  );
}

function DataDumpForGroupTilesWrapper() {
  const shouldFetch = useShouldFetchDataDumpForGroupTiles();
  if (!shouldFetch) {
    return null;
  }
  return (
    <ErrorBoundary
      small={true}
      overlay={true}
      id="MidgardGetters_DataDumpGettersForGroupTiles"
    >
      <DataDumpGettersForGroupTiles />
    </ErrorBoundary>
  );
}

function TimelineDataDumpRowsWrapper() {
  const shouldFetch = useShouldFetchTimelineDataDumpRows();
  if (!shouldFetch) {
    return null;
  }
  return (
    <ErrorBoundary
      small={true}
      overlay={true}
      id="MidgardGetters_DataDumpRowsGetter"
    >
      <DataDumpRowsGetter />
    </ErrorBoundary>
  );
}

function DataSourceGroupsWrapper() {
  const shouldFetch = useShouldFetchDataSourceGroups();
  if (!shouldFetch) {
    return null;
  }
  return (
    <ErrorBoundary
      small={true}
      overlay={true}
      id="MidgardGetters_DataSourceGroupsGetter"
    >
      <DataSourceGroupsGetter />
    </ErrorBoundary>
  );
}

function AlternateTimelinesWrapper() {
  const shouldFetch = useShouldFetchAlternateTimelines();
  if (!shouldFetch) {
    return null;
  }
  return (
    <ErrorBoundary
      small={true}
      overlay={true}
      id="MidgardGetters_AlternateTimelinesGetter"
    >
      <AlternateTimelinesGetter />
    </ErrorBoundary>
  );
}

function LastDataDumpRowWrapper() {
  const shouldFetch = useShouldFetchLastDataDumpRow();
  if (!shouldFetch) {
    return null;
  }
  return (
    <ErrorBoundary
      small={true}
      overlay={true}
      id="MidgardGetters_LastDataDumpRowGetter"
    >
      <LastDataDumpRowGetter />
    </ErrorBoundary>
  );
}

function LastDataDumpRowPerProcessWrapper() {
  const shouldFetch = useShouldFetchLastDataDumpRowPerProcess();
  if (!shouldFetch) {
    return null;
  }
  return (
    <ErrorBoundary
      small={true}
      overlay={true}
      id="MidgardGetters_LastDataDumpRowPerProcessGetter"
    >
      <LastDataDumpRowPerProcessGetter />
    </ErrorBoundary>
  );
}

function WorkSchedulesWrapper() {
  const shouldFetch = useShouldFetchWorkSchedules();
  if (!shouldFetch) {
    return null;
  }
  return (
    <ErrorBoundary
      small={true}
      overlay={true}
      id="MidgardGetters_WorkSchedulesGetter"
    >
      <WorkSchedulesGetter />
    </ErrorBoundary>
  );
}

function CurrentPlaybookWrapper() {
  const shouldFetch = useShouldFetchCurrentPlaybook();
  if (!shouldFetch) {
    return null;
  }
  return (
    <ErrorBoundary
      small={true}
      overlay={true}
      id="MidgardGetters_SkuListGetter"
    >
      <CurrentPlaybookGetter />
    </ErrorBoundary>
  );
}
function useShouldFetchWorkSchedules() {
  const {
    isSchedulePage,
    isGroupTilesPage,
    isProcessTilesPage,
    isTimelinePage,
    isHubletPage,
    isOperatorPage,
    isGridLayoutPage,
    isTrendsPage,
    isReportsPage,
    isSnapshotsPage,
    isTier0Page,
  } = useGetPageFromPath();
  const ckey = useCustomerKey();
  // a work schedule is required to get shifts and process groups
  // (group tiles, process tiles, timeline, hublet, grid, operator, trends) pages
  const shouldFetch =
    isSchedulePage ||
    isGroupTilesPage ||
    isProcessTilesPage ||
    isTimelinePage ||
    isHubletPage ||
    isOperatorPage ||
    isGridLayoutPage ||
    isTrendsPage ||
    isReportsPage ||
    isSnapshotsPage ||
    isTier0Page ||
    window.location.pathname.includes("/snapshots");

  return ckey && shouldFetch;
}

export default DataGetter;

/** we need single-process DDAs on certain pages... */
export function useShouldFetchDataDumpsPerProcess() {
  const visibleGroups = useVisibleGroups();
  const processGroup = visibleGroups[0];
  const isSingleProcessPage = useIsSingleProcessPage();
  const {
    isTrendsPage,
    isProcessTilesPage,
    isTimelinePage,
    isOperatorPage,
    isAllWorkSchedulesPage,
    isGroupTilesPage,
    isHubletPage,
  } = useGetPageFromPath();
  const {
    showCount,
    showSkuId,
    showAnyAheadBehind,
    showOperatorName,
    showSkuName,
    showTeamSize,
  } = useExpandedTileState(null);
  const { isCustomerWithExpandableTile } = useExpandedTileFlags(null);

  const { areProcessTilesExpanded } = useSettings();

  const isMidgardDDAEnabled = useIsMidgardDDAEnabled();
  const [
    {
      general: { extraPnlChartColumns },
    },
  ] = useFlightConfig();
  const isFromSnapshotsPageTimelineOnly = useIsFromSnapshotsPageTimelineOnly();
  if (
    (isAllWorkSchedulesPage && isGroupTilesPage) ||
    (isFromSnapshotsPageTimelineOnly &&
      window.location.search.includes("hideMetricsColumn=true"))
  ) {
    return false;
  }
  if (isOperatorPage || isHubletPage) {
    return false;
  } else if (isSingleProcessPage) {
    return (
      getIsCypressRunning() ||
      extraPnlChartColumns.length > 0 || // single-process - if we need extra pnl chart columns
      isMidgardDDAEnabled
    ); // or if they use midgard DDA for aggregates
  } else if (isProcessTilesPage) {
    const needMidgardDDAForExpandedTile =
      isCustomerWithExpandableTile &&
      areProcessTilesExpanded &&
      (showCount ||
        showSkuId ||
        showAnyAheadBehind ||
        showOperatorName ||
        showSkuName ||
        showTeamSize);

    return (
      processGroup && (isMidgardDDAEnabled || needMidgardDDAForExpandedTile)
    );
  } else if (isTimelinePage) {
    return getIsCypressRunning() || isMidgardDDAEnabled;
  } else if (isTrendsPage) {
    return isMidgardDDAEnabled;
  } else {
    return true;
  }
}

const TRENDS_PAGE_FULL_RANGE_AGGREGATE_LOADING_INTERVAL = 5000;

export function useShouldFetchFullRangeDataDump() {
  const {
    isTrendsPage,
    isOperatorPage,
    isGroupTilesPage,
    isAllWorkSchedulesPage,
    isHubletPage,
  } = useGetPageFromPath();
  const isSingleProcessPage = useIsSingleProcessPage();
  const focusRange = useFocusRange();
  const shouldFetchDataDumpsPerProcess = useShouldFetchDataDumpsPerProcess();
  const processIdsForDataDumpFullRange = useProcessIdsForDataDumpFullRange();
  const visibleProcesses = useVisibleProcesses();
  const isMidgardDDAEnabled = useIsMidgardDDAEnabled();

  // on trends page, slow down fetching to prevent overloading midgard serve
  const isDataDumpGetterLoading = useLoadWhenValuesChange({
    valuesWhichChangeArr: [focusRange, processIdsForDataDumpFullRange],
    loadingInterval: TRENDS_PAGE_FULL_RANGE_AGGREGATE_LOADING_INTERVAL,
    isInitiallyLoading:
      !focusRange || processIdsForDataDumpFullRange.length === 0,
    componentNameForDebugging: "DataDumpGetter",
  });
  const isSerialProcess = useIsSerialProcess();

  const isFullRangeEqualToPerProcess =
    (isSerialProcess || isSingleProcessPage) &&
    shouldFetchDataDumpsPerProcess &&
    visibleProcesses.length === processIdsForDataDumpFullRange.length;

  const [didFetchProcessGroups] = useAtom(didFetchProcessGroupsAtom);
  const [
    {
      general: { extraPnlChartColumns },
    },
  ] = useFlightConfig();
  const [isTrendChartExpandedHubletPage] = useIsTrendChartExpandedHubletPage();
  const [isPnlChartExpandedHubletPage] = useIsPnlChartExpandedHubletPage();
  const isFromSnapshotsPageTimelineOnly = useIsFromSnapshotsPageTimelineOnly();
  if (
    isFromSnapshotsPageTimelineOnly ||
    (isAllWorkSchedulesPage && isGroupTilesPage)
  ) {
    return false;
  }
  const shouldFetch =
    (isMidgardDDAEnabled ||
      isOperatorPage /* operator page always uses midgard */ ||
      extraPnlChartColumns.length > 0) /* extra columns come from midgard */ &&
    focusRange &&
    processIdsForDataDumpFullRange &&
    processIdsForDataDumpFullRange.length > 0 &&
    // don't need full-range if it's equivalent to the per-process data-dump
    !isFullRangeEqualToPerProcess &&
    (isTrendsPage ? !isDataDumpGetterLoading && isMidgardDDAEnabled : true) &&
    (isGroupTilesPage ? didFetchProcessGroups === "fullTilesFetched" : true) &&
    (isHubletPage
      ? isTrendChartExpandedHubletPage || isPnlChartExpandedHubletPage
      : true);

  return shouldFetch;
}

export function useShouldFetchSupervisorContext() {
  const ckey = useCustomerKey();
  const lowercaseCkey = ckey.toLowerCase();
  const { isHubletPage, isGridLayoutPage } = useGetPageFromPath();
  return useMemo(() => {
    const isCustomerWhoUsesSupervisorContext =
      lowercaseCkey in CUSTOMERS_WHO_USE_SUPERVISOR_CONTEXT_TAGGING;

    return (
      isCustomerWhoUsesSupervisorContext &&
      // supervisor context data is used in the PnL Popup reallocation tool
      // (grid, hublet pages -- only allowed for one process at a time)
      (isHubletPage || isGridLayoutPage)
    );
  }, [lowercaseCkey, isHubletPage, isGridLayoutPage]);
}

export function useShouldFetchExpandedTilesData() {
  const { isProcessTilesPage } = useGetPageFromPath();
  const { areProcessTilesExpanded } = useSettings();

  // midgard operatiles on Process Tiles page
  const visibleGroups = useVisibleGroups();
  const processGroup = visibleGroups[0];
  return isProcessTilesPage && areProcessTilesExpanded && processGroup;
}

/** midgard sku data is used in Hublet view (skuCode, skuDisplay, skuScale, teamSize, targetRate, timeWindow, isTPU, callStatus) */
export function useShouldFetchLastDataDumpRow() {
  const isSingleProcessPage = useIsSingleProcessPage();
  const { isHubletPage } = useGetPageFromPath();

  const shouldFetchExpandedTilesData = useShouldFetchExpandedTilesData();
  const visibleProcessIds = useVisibleProcessIds();
  return (
    shouldFetchExpandedTilesData ||
    (isSingleProcessPage && visibleProcessIds.length > 0 && !isHubletPage) // hublet page uses currentSegment from askr instead (Askr returns the whole segment untruncated)
  );
}

/** midgard last-segment data is used in process tiles, timeline pages, (skuCode, skuDisplay, skuScale, teamSize, targetRate, timeWindow, isTPU, callStatus) */
export function useShouldFetchLastDataDumpRowPerProcess() {
  // * could try enabling on group tiles to track call status
  const { isTimelinePage, isProcessTilesPage } = useGetPageFromPath();
  const visibleProcessIds = useVisibleProcessIds();
  return (
    // TODO disabled due to flooding midgard cache with small queries; consider redesigning around this issue; for now, use the full-range DDA.lastSegment on a slower interval instead
    false &&
    !getIsCypressRunning() &&
    (isProcessTilesPage || isTimelinePage) &&
    visibleProcessIds.length > 0
  );
}

export function useIsNoSkuSelectedOnSingleProcessPage() {
  const [lastSegment] = useLastDataDumpRow();
  return lastSegment?.skuCode === "no_sku_selected"; // TODO ?
}

export function useShouldFetchMidgardLotData() {
  const isStorybook = useIsStorybook();
  const [
    {
      general: { enableLotInfoModule: isCustomerWhoUsesLotProgression },
    },
  ] = useFlightConfig();
  const [isLotInfoPaused] = useIsLotInfoPaused();
  return !isLotInfoPaused && isCustomerWhoUsesLotProgression && !isStorybook;
}

export function useShouldFetchAggregatesAndSegments() {
  const {
    isTrendsPage,
    isReportsPage,
    isGroupTilesPage,
    isAllWorkSchedulesPage,
  } = useGetPageFromPath();

  const isTrendsPageFinishedInitialFilter = useSelector(
    state => state.legacy.isTrendsPageFinishedInitialFilter
  );
  const isMidgardDDAEnabled = useIsMidgardDDAEnabled();
  const shouldUseFullRangeDataDump = useShouldFetchFullRangeDataDump();
  const shouldUseDataDumpsForGroupMetrics =
    useShouldUseDataDumpsForGroupMetrics();
  const [
    {
      general: { hideProducingLosses },
    },
  ] = useFlightConfig();
  const midgardIdHubletPage = useMidgardIdHubletPage();
  return useMemo(() => {
    const isTimelineOnlyPage = window.location.pathname.includes("/timeline/");
    if (isTimelineOnlyPage || midgardIdHubletPage) {
      return true;
    }
    if (isReportsPage || (isAllWorkSchedulesPage && isGroupTilesPage)) {
      return false;
    }
    if (isTrendsPage) {
      return isTrendsPageFinishedInitialFilter;
    }
    if (isGroupTilesPage) {
      const notUsingDataDumps =
        !shouldUseDataDumpsForGroupMetrics && !shouldUseFullRangeDataDump;
      const isMidgardDisabled = !isMidgardDDAEnabled;
      return isMidgardDisabled || hideProducingLosses || notUsingDataDumps;
    }
    return true;
  }, [
    isTrendsPageFinishedInitialFilter,
    isReportsPage,
    isGroupTilesPage,
    isAllWorkSchedulesPage,
    isMidgardDDAEnabled,
    shouldUseFullRangeDataDump,
    shouldUseDataDumpsForGroupMetrics,
    hideProducingLosses,
    midgardIdHubletPage,
    isTrendsPage,
  ]);
}

/** Group Tiles use dataDumpsGroupTiles */
export function useShouldFetchDataDumpForGroupTiles() {
  const shouldFetchFullRangeDataDump = useShouldFetchFullRangeDataDump();
  const {
    isProcessTilesPage,
    isTimelinePage,
    isReportsPage,
    isGroupTilesPage,
    isAllWorkSchedulesPage,
  } = useGetPageFromPath();
  const isSingleProcessPage = useIsSingleProcessPage();
  const focusRange = useFocusRange();
  const queryRange = focusRange ? focusRange.lt - focusRange.ge : 0;
  const isTimeRangeInverted = queryRange < 0;
  const isTimeRangeZero = queryRange === 0;

  if (isTimeRangeInverted) {
    console.error(
      `GroupTile time range is inverted! location.pathname: ${
        window.location.pathname
      }, query range ${(queryRange / ONE_HOUR_OF_MS).toFixed(
        1
      )} hours, timeRange: ${JSON.stringify(focusRange)}`
    );
  }
  const [didFetchProcessGroups] = useAtom(didFetchProcessGroupsAtom);

  const isMidgardDDAEnabled = useIsMidgardDDAEnabled();
  const isFromSnapshotsPageTimelineOnly = useIsFromSnapshotsPageTimelineOnly();
  if (
    isFromSnapshotsPageTimelineOnly ||
    (isAllWorkSchedulesPage && isGroupTilesPage)
  ) {
    return false;
  }
  return Boolean(
    // on process tiles page, group tiles DDA is redundant with full-range DDA
    !isReportsPage &&
      !isSingleProcessPage &&
      !(
        (isProcessTilesPage || isTimelinePage) &&
        shouldFetchFullRangeDataDump
      ) &&
      isMidgardDDAEnabled &&
      focusRange &&
      !isTimeRangeInverted &&
      !isTimeRangeZero &&
      (isGroupTilesPage ? didFetchProcessGroups === "fullTilesFetched" : true)
  );
}

export function useShouldFetchLotProgress() {
  const [
    {
      general: { enableLotInfoModule },
    },
  ] = useFlightConfig();
  const { isGridLayoutPage } = useGetPageFromPath();
  return isGridLayoutPage && enableLotInfoModule;
}

/** fetch subordinate DDA on Timeline page for serial lines only or lines where we override the subordinate processes*/
export function useShouldFetchDataDumpSubordinateProcesses() {
  const cKey = useCustomerKey();
  const isSerialProcess = useIsSerialProcess();
  const { isTimelinePage } = useGetPageFromPath();
  const overriddenOtherProcessesPnlChartMachines =
    OVERRIDE_MACHEMETRY_IDS_FOR_OTHER_PROCESSES_PNL_CHART?.[cKey];
  const [
    {
      general: { enableSecondaryProcessesPnlChart },
    },
  ] = useFlightConfig();
  return (
    (isSerialProcess ||
      overriddenOtherProcessesPnlChartMachines ||
      enableSecondaryProcessesPnlChart) &&
    isTimelinePage
  );
}

/** fetch data-dump rows on Hublet page for the SKU / operator timelines (below the timeline) */
export function useShouldFetchTimelineDataDumpRows() {
  const isSingleProcessPage = useIsSingleProcessPage();
  const [isTimelineExpanded] = useIsTimelineExpanded();

  const [
    {
      general: {
        showSkuTimeline,
        showOperatorTimeline,
        timelineTooltipShowCounts,
      },
    },
  ] = useFlightConfig();
  const {
    isTimelinePage,
    isHubletPage,
    isGridLayoutPage,
    isTrendsPage,
    isTier0Page,
    isFromSnapshotsPage,
  } = useGetPageFromPath();
  const { flightOverrideIsMidgardEnabled } = useFlagsWithDefaults();
  const isFromSnapshotsPageTimelineOnly = useIsFromSnapshotsPageTimelineOnly();
  if (
    (isFromSnapshotsPage && flightOverrideIsMidgardEnabled === "disabled") ||
    isFromSnapshotsPageTimelineOnly
  ) {
    return false;
  }
  const isTimelineVisible =
    isTimelinePage || isHubletPage || isGridLayoutPage || isTrendsPage;

  return (
    isTier0Page ||
    (isTimelineVisible && timelineTooltipShowCounts) ||
    (isSingleProcessPage &&
      isTimelineExpanded &&
      (showSkuTimeline || showOperatorTimeline))
  );
}

/** fetch datasourceGroups from askr, so that we can fetch timelines using these as filters */
function useShouldFetchDataSourceGroups() {
  const isAdminUser = useIsAdmin();
  const { isCustomerTilesPage } = useGetPageFromPath();
  const ckey = useCustomerKey();
  const { flightEnableAlternateTimeline } = useFlagsWithDefaults();
  return (
    ckey &&
    !isCustomerTilesPage &&
    (isAdminUser || flightEnableAlternateTimeline)
  );
}
/** fetch alternate timeline segments from askr, so we can display below the timeline to debug ADL rules */
function useShouldFetchAlternateTimelines() {
  const isAdminUser = useIsAdmin();
  const { selectedDataSourceGroups } = useDataSourceGroups();
  const { flightEnableAlternateTimeline } = useFlagsWithDefaults();
  return (
    (isAdminUser || flightEnableAlternateTimeline) &&
    selectedDataSourceGroups.length > 0
  );
}

function useShouldFetchCurrentPlaybook() {
  const { isHubletPage } = useGetPageFromPath();
  const processId = useProcessIdOnSingleProcessPage();
  return isHubletPage && processId;
}

/** when flag flightForceReloadAppOnce becomes true, reload the app once;
 * when flag becomes false, reset the reload key
 */
function ForceReloadAppForCustomer() {
  const { flightForceReloadAppOnce } = useFlagsWithDefaults();
  const [lastReloadedTime, setLastReloadedTime] = useLocalStorageState(
    "lastReloadedTime",
    null as number | null
  );
  const [hasLDClientBeenIdentified] = useAtom(hasLDClientBeenIdentifiedAtom);
  useEffect(() => {
    if (flightForceReloadAppOnce && !lastReloadedTime) {
      setLastReloadedTime(Date.now());
      window.location.reload();
    } else if (
      hasLDClientBeenIdentified &&
      !flightForceReloadAppOnce &&
      lastReloadedTime
    ) {
      setLastReloadedTime(null);
    }
  }, [
    flightForceReloadAppOnce,
    hasLDClientBeenIdentified,
    lastReloadedTime,
    setLastReloadedTime,
  ]);
  return null;
}
